import { format } from 'date-fns';
import { useState } from 'react';
import type { Show } from '../types/show';
import { getDetailedRating, getRatingHistory, calculateRatingStats } from '../lib/api';

interface ShowListItemProps {
  show: Show;
}

interface ParsedScore {
  score: number;
  total: number;
  distribution: {
    one: number;
    two: number;
    three: number;
    four: number;
    five: number;
  };
}

interface RatingStats {
  value: number;
  count: number;
  distribution: number[];
}

interface HistoryItem {
  stats: number[];
  doing_count: number;
  wish_count: number;
  done_count: number;
  fetch_time: string;
  rating?: RatingStats;
  type_ranks?: Array<{
    type: string;
    rank: number;
  }>;
}

function parseDetailedScore(text: string): ParsedScore | null {
  try {
    // 提取评分 - 更新正则表达式以匹配实际格式
    const scoreMatch = text.match(/得分:[-]+>(\d+\.\d+)/);
    // 提取总人数
    const totalMatch = text.match(/总人数:(\d+)/);
    // 提取星级分布
    const distributionMatch = text.match(/1-5星:(\d+)\|(\d+)\|(\d+)\|(\d+)\|(\d+)/);

    if (!scoreMatch || !totalMatch || !distributionMatch) {
      console.error('Failed to parse score data:', text);
      return null;
    }

    return {
      score: parseFloat(scoreMatch[1]),
      total: parseInt(totalMatch[1]),
      distribution: {
        one: parseInt(distributionMatch[1]),
        two: parseInt(distributionMatch[2]),
        three: parseInt(distributionMatch[3]),
        four: parseInt(distributionMatch[4]),
        five: parseInt(distributionMatch[5])
      }
    };
  } catch (error) {
    console.error('Error parsing detailed score:', error);
    return null;
  }
}

export function ShowListItem({ show }: ShowListItemProps) {
  const [detailedStats, setDetailedStats] = useState<string>('');
  const [rawDetailedStats, setRawDetailedStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [ratingHistory, setRatingHistory] = useState<any[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryData, setSelectedHistoryData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [multipliedItems, setMultipliedItems] = useState<Set<number>>(new Set());

  const handleGetDetailedStats = async () => {
    try {
      setIsLoading(true);
      const details = await getDetailedRating(show.id);
      setRawDetailedStats(details);
      setDetailedStats(details.calculated_stats.formatted);
      setShowModal(true);
    } catch (error) {
      console.error('获取详细评分失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const data = await getRatingHistory(show.id);
      
      console.log('[ShowListItem] History data received:', {
        id: show.id,
        title: show.title,
        type: show.type,
        historyCount: data.history?.length,
        firstItem: data.history?.[0]
      });

      if (!data.history || data.history.length === 0) {
        setErrorMessage('暂无历史记录');
        return;
      }

      // 直接使用原始历史数据，确保与getDetailedRating计算逻辑完全一致
      const processedHistory = data.history.map((item: any) => {
        console.log('[ShowListItem] Processing history item for', show.title, ':', {
          fetch_time: item.fetch_time,
          doing_count: item.doing_count,
          done_count: item.done_count,
          type_ranks: item.type_ranks,
          stats: item.stats
        });

        // 与getDetailedRating保持完全一致的targetCount计算逻辑
        const targetCount = item.type_ranks?.[0]?.type.includes('片') ? item.done_count : item.doing_count;
        
        console.log('[ShowListItem] Using targetCount:', targetCount, 'for type:', item.type_ranks?.[0]?.type);
        
        // 使用您的原始算法计算真实评分统计
        const ratingStats = calculateRatingStats(item.stats, targetCount);

        console.log('[ShowListItem] Calculated rating stats:', {
          score: ratingStats.score,
          totalCount: ratingStats.totalCount,
          targetCount: targetCount
        });

        return {
          ...item,
          rating: {
            value: ratingStats.score,
            count: ratingStats.totalCount,
            distribution: item.stats
          }
        };
      });

      setRatingHistory(processedHistory);
      setShowHistoryModal(true);
    } catch (error: any) {
      console.error('获取评分历史失败:', error);
      setErrorMessage('获取历史记录失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectHistoryItem = (historyItem: HistoryItem) => {
    if (!historyItem.rating) return;
    
    setSelectedHistoryData({
      ...historyItem,
      rating: {
        value: historyItem.rating.value,
        count: historyItem.rating.count,
        distribution: historyItem.stats || historyItem.rating.distribution
      }
    });
    setShowHistoryModal(false);
    setShowModal(true);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      console.error('❌ Error formatting date:', { dateString, error });
      return 'Invalid Date';
    }
  };

  const renderRatingBar = () => {
    if (!show.detailedScore || typeof show.detailedScore !== 'string') return null;
    
    const parsedScore = parseDetailedScore(show.detailedScore);
    if (!parsedScore) return null;

    const { score, total, distribution } = parsedScore;
    const bars = [5, 4, 3, 2, 1].map(star => {
      const count = distribution[star === 1 ? 'one' : star === 2 ? 'two' : star === 3 ? 'three' : star === 4 ? 'four' : 'five'];
      const percentage = ((count / total) * 100).toFixed(1);
      return { star, percentage, count };
    });

    return (
      <div className="mt-2 space-y-1">
        <div className="text-xs text-gray-500 mb-1">评分分布：</div>
        {bars.map(({ star, percentage, count }) => (
          <div key={star} className="flex items-center gap-2 text-xs">
            <span className="w-8 text-gray-600">{star}星</span>
            <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
              <div
                className="h-full bg-yellow-400"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="w-16 text-gray-500">{count.toLocaleString()}人</span>
            <span className="w-12 text-gray-600">{percentage}%</span>
          </div>
        ))}
      </div>
    );
  };

  const parsedScore = show.detailedScore ? parseDetailedScore(show.detailedScore) : null;

  const handleMultiplyCount = (index: number) => {
    const newMultipliedItems = new Set(multipliedItems);
    if (multipliedItems.has(index)) {
      // 如果已经乘以2了，则恢复原值（除以2）
      newMultipliedItems.delete(index);
    } else {
      // 乘以2
      newMultipliedItems.add(index);
    }
    setMultipliedItems(newMultipliedItems);
  };

  const getDisplayCount = (item: any, index: number) => {
    const originalCount = item.rating?.count || 0;
    return multipliedItems.has(index) ? originalCount * 2 : originalCount;
  };

  return (
    <div className="p-4 hover:bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900">
              <a href={`https://movie.douban.com/subject/${show.id}/`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                {show.title}
              </a>
            </h3>
            <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
              {show.type === 'movie' ? '电影' : show.category === 'short' ? '短剧' : '剧集'}
            </span>
          </div>
          
          {show.subtitle && (
            <p className="mt-1 text-sm text-gray-500">{show.subtitle}</p>
          )}

          <div className="mt-2 space-y-1">
            {show.directors && show.directors.length > 0 && (
              <p className="text-sm text-gray-500">
                导演：{show.directors.join(', ')}
              </p>
            )}

            {show.actors && show.actors.length > 0 && (
              <p className="text-sm text-gray-500">
                演员：{show.actors.join(', ')}
              </p>
            )}
          </div>

          {renderRatingBar()}
        </div>

        <div className="ml-6 flex flex-col items-end">
          <div className="text-2xl font-semibold text-yellow-500">
            {parsedScore ? parsedScore.score.toString() : '暂无评分'}
          </div>
          {parsedScore && (
            <div className="text-sm text-gray-500">
              {parsedScore.total.toLocaleString()} 人评价
            </div>
          )}
          <div className="text-sm text-gray-500">
            {show.ratingCount?.toLocaleString() || '未知'} 人看过
          </div>
          <button
            onClick={handleGetDetailedStats}
            disabled={isLoading}
            className="mt-2 px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-300"
          >
            {isLoading ? '获取中...' : '详细评分'}
          </button>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
        <span>创建时间：{formatDate(show.time)}</span>
        {show.rating_time ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewHistory}
              className="text-blue-600 hover:text-blue-800"
              disabled={isLoading}
            >
              出分时间：{formatDate(show.rating_time)}
            </button>
            {errorMessage && (
              <span className="text-red-500">{errorMessage}</span>
            )}
          </div>
                  ) : (
            <div className="flex items-center gap-2">
              <span className="text-gray-400">未出分</span>
              {show.last_updated && (
                <button
                  onClick={handleViewHistory}
                  className="text-xs text-blue-600 hover:text-blue-800"
                  disabled={isLoading}
                >
                  (最后查询：{formatDate(show.last_updated)})
                </button>
              )}
              {errorMessage && (
                <span className="text-red-500">{errorMessage}</span>
              )}
            </div>
          )}
      </div>

      {/* 历史记录弹窗 */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4">
            <h3 className="text-lg font-medium mb-4">{show.title} - 评分历史记录</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {ratingHistory.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded">
                  <button
                    onClick={() => handleSelectHistoryItem(item)}
                    className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="text-lg font-semibold text-yellow-500">
                        {item.rating?.value || '暂无评分'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(item.fetch_time)}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      总评分人数：{getDisplayCount(item, index).toLocaleString()}
                      <span className="ml-4">在看人数：{item.doing_count?.toLocaleString() || 0}</span>
                      <span className="ml-4">看过人数：{item.done_count?.toLocaleString() || 0}</span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {item.rating?.distribution.map((ratio: number, starIndex: number) => {
                        const star = starIndex + 1;
                        const count = Math.round(ratio * getDisplayCount(item, index));
                        const percentage = (ratio * 100).toFixed(1);
                        return (
                          <div key={star} className="flex items-center gap-2 text-xs">
                            <span className="w-8 text-gray-600">{star}星</span>
                            <div className="flex-1 h-1.5 bg-gray-100 rounded overflow-hidden">
                              <div
                                className="h-full bg-yellow-400"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="w-16 text-gray-500">{count.toLocaleString()}人</span>
                            <span className="w-12 text-gray-600">{percentage}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </button>
                  <div className="px-3 pb-2 flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMultiplyCount(index);
                      }}
                      className={`text-xs px-2 py-1 rounded ${
                        multipliedItems.has(index)
                          ? 'bg-orange-100 text-orange-600 border border-orange-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-300'
                      } hover:bg-opacity-80 transition-colors`}
                    >
                      {multipliedItems.has(index) ? '恢复' : 'x2'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-between items-center">
              <div className="text-xs text-gray-500">
                💡 点击 "x2" 可将人数翻倍显示，点击 "恢复" 可还原
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setMultipliedItems(new Set()); // 关闭弹窗时重置
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 详细评分弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">{show.title} - 详细评分</h3>
              {selectedHistoryData && (
                <div className="text-sm text-gray-500">
                  数据日期：{formatDate(selectedHistoryData.fetch_time)}
                </div>
              )}
            </div>
            <div className="space-y-4">
              {/* 评分和总人数 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">评分</p>
                  <p className="text-2xl font-semibold text-yellow-500">
                    {selectedHistoryData ? 
                      selectedHistoryData.rating.value.toFixed(2) :
                      detailedStats.match(/得分:[-]+>(\d+\.?\d*)/)?.[1] || '暂无评分'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">总人数</p>
                  <p className="text-xl font-semibold text-gray-700">
                    {selectedHistoryData ?
                      selectedHistoryData.rating.count.toLocaleString() :
                      detailedStats.match(/总人数:(\d+)/)?.[1]?.toLocaleString() || 0
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">在看人数</p>
                  <p className="text-xl font-semibold text-gray-700">
                    {selectedHistoryData ?
                      selectedHistoryData.doing_count.toLocaleString() :
                      rawDetailedStats?.doing_count?.toLocaleString() || 0
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">看过人数</p>
                  <p className="text-xl font-semibold text-gray-700">
                    {selectedHistoryData ?
                      selectedHistoryData.done_count.toLocaleString() :
                      rawDetailedStats?.done_count?.toLocaleString() || 0
                    }
                  </p>
                </div>
              </div>

              {/* 评分分布 */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">评分分布</h4>
                {(() => {
                  let distributionData: { [key: number]: number } = {};
                  if (selectedHistoryData) {
                    const dist = selectedHistoryData.stats || selectedHistoryData.rating?.distribution;
                    // 对于电影使用 done_count，对于剧集使用 doing_count
                    const total = selectedHistoryData.type_ranks?.[0]?.type.includes('片') 
                      ? selectedHistoryData.done_count 
                      : selectedHistoryData.doing_count;
                    if (dist) {
                      distributionData = {
                        1: Math.round(dist[0] * total),
                        2: Math.round(dist[1] * total),
                        3: Math.round(dist[2] * total),
                        4: Math.round(dist[3] * total),
                        5: Math.round(dist[4] * total)
                      };
                    }
                  } else {
                    const match = detailedStats.match(/1-5星:(\d+)\|(\d+)\|(\d+)\|(\d+)\|(\d+)/);
                    if (!match) return null;
                    distributionData = {
                      1: Number(match[1]),
                      2: Number(match[2]),
                      3: Number(match[3]),
                      4: Number(match[4]),
                      5: Number(match[5])
                    };
                  }

                  const total = Object.values(distributionData).reduce((a, b) => a + b, 0);

                  return [5, 4, 3, 2, 1].map((star) => {
                    const count = distributionData[star];
                    const percentage = ((count / total) * 100).toFixed(1);
                    
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="w-8 text-sm text-gray-600">{star}星</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded">
                          <div
                            className="h-full bg-yellow-400 rounded"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="w-16 text-gray-500">{count.toLocaleString()}人</span>
                        <span className="w-12 text-sm text-gray-600">{percentage}%</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {selectedHistoryData && (
                <button
                  onClick={() => {
                    setSelectedHistoryData(null);
                    setShowModal(false);
                    setShowHistoryModal(true);
                  }}
                  className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  返回历史记录
                </button>
              )}
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedHistoryData(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 